// Partner cockpit — implementation-program drill-in (B5B-PARTNER-COCKPIT §6). One program's detail inside
// the cockpit chrome: meta + timeline + its sprint-gate decision history. Mandate re-checked server-side
// (403 if out of scope). Read-only. Behind real auth.
import { useParams, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Briefcase, CheckCircle2, XCircle, CircleDot } from "lucide-react";
import { cockpit, CockpitError } from "../lib/cockpit";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  active: "default", in_progress: "default", completed: "secondary", draft: "outline",
};
function fmt(d: string | null): string { return d ? new Date(d).toLocaleDateString() : "—"; }

function GateIcon({ decision }: { decision: string }) {
  if (/pass|go|approved/i.test(decision)) return <CheckCircle2 className="size-4 text-emerald-700" />;
  if (/fail|no_go|rejected|blocked/i.test(decision)) return <XCircle className="size-4 text-destructive" />;
  return <CircleDot className="size-4 text-muted-foreground" />;
}

export function ProgramDetailRoute() {
  const { programId } = useParams({ from: "/program/$programId" });
  const { data, isLoading, error } = useQuery({ queryKey: ["cockpit-program", programId], queryFn: () => cockpit.program(programId), retry: false });

  if (error instanceof CockpitError && (error.status === 403 || error.status === 404)) {
    return <div className="flex flex-col gap-4"><PageHeader title="Program" /><Card><CardContent className="py-8 text-center text-sm text-destructive">This program is outside your mandate.</CardContent></Card></div>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild size="sm" variant="ghost"><Link to="/programs"><ArrowLeft className="size-4" /> Programs</Link></Button>
      </div>
      {isLoading && <Skeleton className="h-24" />}
      {error && !(error instanceof CockpitError && (error.status === 403 || error.status === 404)) && (
        <Card><CardContent className="py-8 text-center text-sm text-destructive">{(error as Error).message}</CardContent></Card>
      )}
      {data && (
        <>
          <PageHeader title={data.name} description={data.tenantName} />
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={STATUS_VARIANT[data.status] ?? "secondary"}>{humanize(data.status)}</Badge>
            <Badge variant="outline">{data.tenantName}</Badge>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Started</span><span>{fmt(data.startedAt)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Target completion</span><span>{fmt(data.expectedCompletionAt)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Completed</span><span>{fmt(data.completedAt)}</span></div>
                {data.description && <p className="whitespace-pre-line border-t pt-2 text-muted-foreground">{data.description}</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Briefcase className="size-4" />Sprint-gate history</CardTitle></CardHeader>
              <CardContent>
                {data.gates.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No sprint-gate decisions recorded.</p>
                ) : (
                  <ul className="m-0 list-none space-y-2 p-0 text-sm">
                    {data.gates.map((g) => (
                      <li key={g.id} className="flex items-start justify-between gap-3 border-b py-1.5 last:border-b-0">
                        <span className="flex min-w-0 items-center gap-2">
                          <GateIcon decision={g.decision} />
                          <span className="min-w-0">
                            <span className="font-medium">Sprint {g.sprintNumber}{g.sprintStageCode ? ` · ${humanize(g.sprintStageCode)}` : ""}</span>
                            <span className="text-muted-foreground"> — {humanize(g.decision)}</span>
                            {g.rationale && <span className="block truncate text-xs text-muted-foreground">{g.rationale}</span>}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">{fmt(g.decidedAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
